import * as React from 'react';

import type { CameraInfo } from '@rematter/frontend-lib-api-client/grpc/sidecar/v1/camera_local_api_pb';
import { StreamProfile } from '@rematter/frontend-lib-api-client/grpc/sidecar/v1/camera_local_api_pb';
import { CameraStream } from '@rematter/ui-web';
import { useAtomValue } from 'jotai';

import type { ScaleSidecar } from '~/shared/graphql/queries/useScalesQuery';
import { useCameraLocalClient } from '~/shared/providers/libp2p/useCameraLocalClient';
import { useHardwareSidecarConnection } from '~/shared/providers/libp2p/useHardwareSidecarConnection';
import { useHardwarePresenceMembers } from '~/shared/providers/presence';
import { $globalScalesQuery } from '~/shared/state/scales.state';

export type ScaleCameraStreamHandle = {
  takeSnapshot: () => Promise<string | null>;
};

type ScaleCameraStreamProps = {
  cameraId: string;
  sidecarId: string;
  streamProfile: string | null;
  cameraSidecar: ScaleSidecar | null;
};

export const ScaleCameraStream = React.forwardRef<
  ScaleCameraStreamHandle,
  ScaleCameraStreamProps
>(({ cameraId, sidecarId, streamProfile, cameraSidecar }, ref) => {
  const [cameraInfo, setCameraInfo] = React.useState<CameraInfo | null>(null);
  const { refetch: refetchScales } = useAtomValue($globalScalesQuery);

  const presenceMembers = useHardwarePresenceMembers();
  const isOnline = presenceMembers.has(cameraId);

  const { isConnecting } = useHardwareSidecarConnection({
    sidecar: cameraSidecar,
    onConnectionFailed: refetchScales,
  });

  const cameraClient = useCameraLocalClient(sidecarId);

  React.useEffect(() => {
    if (!cameraClient) return;

    let cancelled = false;

    cameraClient
      .getCameraInfo({ cameraId })
      .then((response) => {
        if (cancelled || !response.camera) return;
        setCameraInfo(response.camera);
      })
      .catch((err) => {
        console.warn('Failed to get camera info via RPC', err);
      });

    return () => {
      cancelled = true;
    };
  }, [cameraClient, cameraId]);

  React.useImperativeHandle(
    ref,
    () => ({
      takeSnapshot: async () => {
        if (!cameraClient) return null;
        try {
          const response = await cameraClient.takeSnapshot({
            cameraId,
            layers: [],
          });
          return response.snapshots[0]?.fileId ?? null;
        } catch (err) {
          console.warn('Failed to take snapshot', err);
          return null;
        }
      },
    }),
    [cameraClient, cameraId],
  );

  const negotiateStream = React.useCallback(
    async (ch: number, sdpOffer: string) => {
      if (!cameraClient) return '';
      const resolvedProfile = cameraInfo?.streamProfile ?? streamProfile;
      const profileEnum =
        resolvedProfile === 'main' ? StreamProfile.MAIN : StreamProfile.SUB;
      const response = await cameraClient.startCameraStream({
        cameraId,
        sdpOffer,
        channel: ch,
        streamProfile: profileEnum,
      });
      return response.sdpAnswer;
    },
    [cameraClient, cameraId, cameraInfo?.streamProfile, streamProfile],
  );

  const isStreamReady =
    !!cameraClient &&
    !!cameraInfo &&
    isOnline &&
    cameraInfo.phase !== 'wrong_credentials' &&
    (cameraInfo.phase === 'operational' || cameraInfo.phase === '');

  const channelCount = cameraInfo?.channelCount || 1;

  if (!cameraClient) {
    return (
      <div className="flex aspect-video w-full items-center justify-center rounded bg-neutral/10 text-sm text-neutral/40">
        {isConnecting ? 'Connecting...' : 'Sidecar offline'}
      </div>
    );
  }

  if (!isOnline) {
    return (
      <div className="flex aspect-video w-full items-center justify-center rounded bg-neutral/10 text-sm text-neutral/40">
        Camera offline
      </div>
    );
  }

  return (
    <CameraStream
      channelCount={channelCount}
      channel={0}
      negotiateStream={negotiateStream}
      disabled={!isStreamReady}
      className="aspect-video w-full overflow-hidden rounded"
    />
  );
});
